import { Target, Clock, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react'

import { WORKFLOW_STAGE_LABELS, WORKFLOW_STAGE_COLORS } from '@/components/pm/gantt/types'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  useEstimationAccuracy,
  formatPercent,
  type AnalyticsFilters,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface EstimationAccuracyChartProps {
  filters?: AnalyticsFilters
  className?: string
}

/**
 * 估算准确率图表组件
 * Requirements: 4.1, 4.2, 4.4
 */
export function EstimationAccuracyChart({
  filters = {},
  className,
}: EstimationAccuracyChartProps) {
  const { data: accuracy, isLoading, error } = useEstimationAccuracy(filters)

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            估算准确率
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Clock className="h-5 w-5 animate-spin mr-2" />
            加载中...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            估算准确率
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-5 w-5 mr-2" />
            加载失败
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!accuracy) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            估算准确率
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            暂无数据
          </div>
        </CardContent>
      </Card>
    )
  }

  const getAccuracyColor = (value: number) => {
    if (value >= 0.8) {return 'text-green-600'}
    if (value >= 0.6) {return 'text-yellow-600'}
    return 'text-red-600'
  }

  const getAccuracyBadge = (value: number) => {
    if (value >= 0.8) {return { variant: 'default' as const, label: '优秀' }}
    if (value >= 0.6) {return { variant: 'secondary' as const, label: '良好' }}
    return { variant: 'destructive' as const, label: '需改进' }
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          估算准确率
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 整体准确率 */}
        <div className="text-center p-4 bg-muted/50 rounded-lg">
          <div className="text-3xl font-bold mb-1">
            <span className={getAccuracyColor(accuracy.overall)}>
              {formatPercent(accuracy.overall)}
            </span>
          </div>
          <div className="text-sm text-muted-foreground">整体估算准确率</div>
          <Badge {...getAccuracyBadge(accuracy.overall)} className="mt-2">
            {getAccuracyBadge(accuracy.overall).label}
          </Badge>
        </div>

        {/* 按阶段准确率 */}
        {accuracy.byStage.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">按阶段</h4>
            {accuracy.byStage.map((item) => (
              <div key={item.stage} className="flex items-center gap-3">
                <div
                  className={cn(
                    'w-3 h-3 rounded-full shrink-0',
                    WORKFLOW_STAGE_COLORS[item.stage]
                  )}
                />
                <span className="text-sm flex-1 truncate">
                  {WORKFLOW_STAGE_LABELS[item.stage]}
                </span>
                <Progress value={item.accuracy * 100} className="w-24 h-2" />
                <span
                  className={cn('text-sm font-medium w-14 text-right', getAccuracyColor(item.accuracy))}
                >
                  {formatPercent(item.accuracy)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* 按成员准确率排名 */}
        {accuracy.byMember.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">成员排名</h4>
            {accuracy.byMember.slice(0, 5).map((member, index) => (
              <div key={member.employeeId} className="flex items-center gap-3">
                <span
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium',
                    index === 0
                      ? 'bg-yellow-100 text-yellow-800'
                      : index === 1
                        ? 'bg-gray-100 text-gray-800'
                        : index === 2
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-muted text-muted-foreground'
                  )}
                >
                  {index + 1}
                </span>
                <span className="text-sm flex-1 truncate">{member.employeeName}</span>
                <span
                  className={cn('text-sm font-medium', getAccuracyColor(member.accuracy))}
                >
                  {formatPercent(member.accuracy)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* 趋势 */}
        {accuracy.trend.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">近期趋势</h4>
            <div className="flex items-center gap-2">
              {accuracy.trend.length >= 2 && (
                <>
                  {accuracy.trend[accuracy.trend.length - 1].accuracy >
                  accuracy.trend[0].accuracy ? (
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-600" />
                  )}
                  <span className="text-sm text-muted-foreground">
                    {accuracy.trend[accuracy.trend.length - 1].accuracy >
                    accuracy.trend[0].accuracy
                      ? '准确率上升中'
                      : '准确率下降中'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
